/**
 * Helper para parsear archivos CSV de solicitudes de Seguros Mercantil
 * Formato esperado: separador punto y coma (;), 26 columnas fijas
 */

/**
 * Limpia un valor de texto eliminando espacios y caracteres especiales
 * @param {string} valor - Valor a limpiar
 * @returns {string} - Valor limpio
 */
const limpiarValor = (valor) => {
  if (!valor || valor === "N/A") return "";
  return valor.trim();
};

/**
 * Extrae el número de cédula manteniendo el prefijo V- o E-, eliminando solo "VEN "
 * @param {string} cedulaConPrefijo - Cédula con posible prefijo
 * @returns {string} - Cédula con prefijo V- o E- (ej: "V-5346421")
 */
const extraerCedulaSinPrefijo = (cedulaConPrefijo) => {
  if (!cedulaConPrefijo || cedulaConPrefijo === "N/A") return "";
  // Solo eliminar "VEN " al inicio, mantener V- o E-
  return cedulaConPrefijo
    .replace(/^VEN\s+/i, "") // Eliminar "VEN " al inicio (case insensitive)
    .trim();
};

/**
 * Parsea la cadena de medicamentos separada por comas
 * @param {string} medicamentosStr - String con medicamentos separados por coma
 * @returns {Array<{nombre: string, enRecipe: boolean}>} - Array de medicamentos
 */
const parsearMedicamentos = (medicamentosStr) => {
  if (!medicamentosStr || medicamentosStr === "N/A") return [];

  // Separar por comas y limpiar cada medicamento
  return medicamentosStr
    .split(",")
    .map((med) => med.trim())
    .filter((med) => med.length > 0)
    .map((med) => ({
      nombre: med,
      cantidad: null, // Sin cantidad por defecto - el usuario la ingresa manualmente si desea
      enRecipe: true, // Los medicamentos del CSV vienen de la orden autorizada
    }));
};

/**
 * Formatea el detalle de medicamentos para el backend
 * @param {Array<{nombre: string, enRecipe: boolean}>} medicamentos - Array de medicamentos
 * @returns {string} - String formateado con ✔️ y saltos de línea
 */
const formatearDetalleMedicamentos = (medicamentos) => {
  if (!medicamentos || medicamentos.length === 0) return "";

  return medicamentos
    .map((med) => {
      const cantidadStr = med.cantidad ? ` - Cant: ${med.cantidad}` : "";
      return `${med.enRecipe ? "✔️" : "❌"} ${med.nombre}${cantidadStr}`;
    })
    .join("\n");
};

/**
 * Parsea el contenido de un archivo CSV de solicitud Mercantil
 * @param {string} csvContent - Contenido del archivo CSV
 * @returns {Object} - Objeto con los datos mapeados
 */
export const parsearCSVSolicitud = (csvContent) => {
  if (!csvContent || typeof csvContent !== "string") {
    throw new Error("El contenido del CSV es inválido");
  }

  // Eliminar BOM si existe y normalizar saltos de línea
  const contenidoLimpio = csvContent
    .replace(/^\uFEFF/, "") // Eliminar BOM
    .replace(/\r\n/g, "\n") // Normalizar Windows
    .replace(/\r/g, "\n"); // Normalizar Mac antiguo

  // Separar en líneas y filtrar vacías
  const lineas = contenidoLimpio.split("\n").filter((linea) => linea.trim());

  if (lineas.length === 0) {
    throw new Error("El archivo CSV está vacío");
  }

  // Tomar la primera línea con datos (ignorar encabezados si los hubiera)
  // En este formato, la primera línea puede ser encabezados o datos
  // Verificaremos si parece una orden válida
  const primeraLinea = lineas[0];
  const segundaLinea = lineas[1];

  // Si la primera línea tiene texto descriptivo (encabezados), usar la segunda
  const datosLinea =
    primeraLinea.includes("de orden") && segundaLinea
      ? segundaLinea
      : primeraLinea;

  // Separar por punto y coma
  const columnas = datosLinea.split(";");

  if (columnas.length < 26) {
    throw new Error(
      `El CSV debe tener al menos 26 columnas. Se encontraron ${columnas.length} columnas.`
    );
  }

  // Extraer valores según el mapeo definido
  const identificador = limpiarValor(columnas[0]); // # de orden (1)
  const solicitante = limpiarValor(columnas[1]); // Asegurado / Beneficiario (2)
  const solicitanteCedulaConPrefijo = limpiarValor(columnas[2]); // Nro de documento del asegurado (3)
  const grupoDiagnostico = limpiarValor(columnas[6]); // Grupo Diagnóstico (7)
  const medicamentosStr = limpiarValor(columnas[14]); // Medicamentos Solicitados (15)
  const titularCedulaRaw = limpiarValor(columnas[23]); // Nro de Documento (24)
  const titular = limpiarValor(columnas[24]); // Nombre Completo (25)

  // Validaciones mínimas
  if (!identificador) {
    throw new Error("No se encontró el número de orden en el CSV");
  }

  // Parsear medicamentos
  const medicamentos = parsearMedicamentos(medicamentosStr);

  // Construir objeto de retorno con los campos necesarios
  const resultado = {
    // Paso 1 - Datos del Paciente
    identificador,
    tipo: "Aguda", // Siempre Aguda según especificación
    titular: titular || solicitante, // Usar titular o solicitante como fallback
    titularCedula: extraerCedulaSinPrefijo(titularCedulaRaw),
    solicitante,
    solicitanteCedula: extraerCedulaSinPrefijo(solicitanteCedulaConPrefijo),

    // Paso 2 - Datos Médicos
    diagnostico: grupoDiagnostico,

    // Paso 3 - Medicamentos
    medicamentos,
    detalle: formatearDetalleMedicamentos(medicamentos),

    // Metadata útil para feedback
    _meta: {
      totalMedicamentos: medicamentos.length,
      tieneDiagnostico: !!grupoDiagnostico,
      tieneCedulaSolicitante: !!solicitanteCedulaConPrefijo,
    },
  };

  return resultado;
};

/**
 * Valida si un archivo parece ser un CSV válido de solicitud Mercantil
 * @param {File} file - Archivo a validar
 * @returns {Promise<boolean>} - true si parece válido
 */
export const validarArchivoCSV = async (file) => {
  if (!file) return false;

  // Validar extensión
  const extension = file.name.split(".").pop().toLowerCase();
  if (extension !== "csv") {
    throw new Error("El archivo debe ser de tipo CSV");
  }

  // Validar tamaño (máximo 1MB para archivos de texto)
  if (file.size > 1024 * 1024) {
    throw new Error("El archivo es demasiado grande (máximo 1MB)");
  }

  return true;
};

/**
 * Lee un archivo como texto
 * @param {File} file - Archivo a leer
 * @returns {Promise<string>} - Contenido del archivo
 */
export const leerArchivoComoTexto = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Error al leer el archivo"));
    reader.readAsText(file, "UTF-8");
  });
};

export default {
  parsearCSVSolicitud,
  validarArchivoCSV,
  leerArchivoComoTexto,
  parsearMedicamentos,
  formatearDetalleMedicamentos,
};
